import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Textarea } from '@/components/ui/textarea';
import { Plus, FileText, Send, Download, Trash2, Eye, Search, CheckCircle, ShoppingCart, AlertCircle, CalendarIcon, ChevronRight, Pencil, RefreshCw, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const STATUS_COLORS = {
  draft: 'bg-slate-100 text-slate-700',
  pending_approval: 'bg-amber-100 text-amber-700',
  approved: 'bg-emerald-100 text-emerald-700',
  sent: 'bg-blue-100 text-blue-700',
  acknowledged: 'bg-indigo-100 text-indigo-700',
  provider_processed: 'bg-cyan-100 text-cyan-700',
  ra_generated: 'bg-purple-100 text-purple-700',
  constraint: 'bg-red-100 text-red-700',
  ready_for_delivery: 'bg-green-100 text-green-700',
  delivered: 'bg-emerald-600 text-white'
};

const STATUS_LABELS = {
  draft: 'Draft',
  pending_approval: 'Pending Approval',
  approved: 'Approved',
  sent: 'Sent',
  acknowledged: 'Acknowledged',
  provider_processed: 'Provider Processed',
  ra_generated: 'RA Generated',
  constraint: 'Constraint',
  ready_for_delivery: 'Ready for Delivery',
  delivered: 'Delivered'
};

// Inline RA Number Input component
function RANumberInput({ poId, onSave }) {
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const handleSave = async () => {
    if (!value.trim()) return;
    setSaving(true);
    await onSave(poId, value);
    setSaving(false);
    setValue('');
  };
  return (
    <div className="flex items-center gap-1">
      <Input
        value={value}
        onChange={e => setValue(e.target.value)}
        placeholder="Enter RA No."
        className="h-7 text-xs w-28"
        data-testid={`ra-input-${poId}`}
      />
      <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={handleSave} disabled={saving || !value.trim()} data-testid={`ra-save-${poId}`}>
        {saving ? '...' : 'Save'}
      </Button>
    </div>
  );
}

export default function PurchaseOrders() {
  const { API, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [circuits, setCircuits] = useState([]);
  const [dataCenters, setDataCenters] = useState({});
  const [vendorSettings, setVendorSettings] = useState({ vendor_emails: {} });
  const [loading, setLoading] = useState(true);
  const [createDialog, setCreateDialog] = useState(false);
  const [viewDialog, setViewDialog] = useState({ open: false, po: null });
  const [settingsDialog, setSettingsDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [vendorFilter, setVendorFilter] = useState('all');
  
  // New dialogs for edit PO number and status update
  const [editPONumberDialog, setEditPONumberDialog] = useState({ open: false, po: null, newPONumber: '' });
  const [updateStatusDialog, setUpdateStatusDialog] = useState({ open: false, po: null, newStatus: '', reason: '', ra_number: '' });
  // Remap dialog: re-link a PO to a different customer/circuit (e.g. after duplicate cleanup)
  const [remapDialog, setRemapDialog] = useState({ open: false, po: null, customer_id: '', circuit_id: '', submitting: false });
  const [remapCircuits, setRemapCircuits] = useState([]);
  const [remapCircuitsLoading, setRemapCircuitsLoading] = useState(false);
  // Acceptance email dialog: allows picking acceptance date before sending
  const todayYMD = () => new Date().toISOString().split('T')[0];
  const [acceptanceDialog, setAcceptanceDialog] = useState({
    open: false,
    po: null,
    mode: 'send',          // 'send' for delivered POs | 'mark_send' for backfill
    acceptance_date: todayYMD(),
    to_str: '',
    cc_str: '',
    submitting: false,
  });
  const [defaultRecipients, setDefaultRecipients] = useState({ to: [], cc: [] });

  // Load the default recipients once for placeholder/pre-fill
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/vendor-po/acceptance-email/defaults`)
      .then(res => { if (alive && res.data) setDefaultRecipients({ to: res.data.to || [], cc: res.data.cc || [] }); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  // Helper function to check if user has any of the allowed roles
  const hasRole = (allowedRoles) => {
    const userRoles = user?.roles || (user?.role ? [user.role] : []);
    return allowedRoles.some(role => userRoles.includes(role));
  };

  const isAdmin = hasRole(['admin']);
  const canEditPONumber = hasRole(['admin', 'accounts']);
  const canUpdateStatus = hasRole(['admin', 'accounts', 'delivery', 'onboarding']);
  const isDeliveryOnly = hasRole(['delivery']) && !hasRole(['admin', 'accounts']);
  const canCreatePO = hasRole(['admin', 'accounts']);

  const [formData, setFormData] = useState({
    vendor: 'GTPL',
    customer_id: '',
    circuit_id: '',
    bandwidth_mbps: '',
    end_a_location: '',
    manual_arc: '',
    po_date: new Date(),
    attention_to: '',
    contract_period_months: 12,
    delivery_timeline: '3-4 Weeks',
    static_ip: '',
    drop_interface: '',
    bgp_required: '',
  });

  const [anonetBtsLocations, setAnonetBtsLocations] = useState({});

  // Anonet Settings dialog state
  const [anonetSettings, setAnonetSettings] = useState({
    bts_locations: [],      // [{_id, key, code, address}] — _id is React-stable
    price_slabs: [],        // [{min_bandwidth, max_bandwidth, rate_per_mbps, manual_required}]
    emails_to: '',
    emails_cc: '',
  });
  const [anonetSettingsLoading, setAnonetSettingsLoading] = useState(false);

  // Anonet email send dialog state
  const todayYMD2 = () => new Date().toISOString().split('T')[0];
  const [anonetSendDialog, setAnonetSendDialog] = useState({
    open: false, po: null, submitting: false,
    to_str: '', cc_str: '',
    latitude: '', longitude: '',
    address_proof_file_id: '',
    address_proof_filename: '',
    address_proof_b64: '',
    address_proof_mime: '',
    saf_purpose: 'Internet/Bandwidth Access',
    saf_b_contact_name: '',
    saf_b_dept_name: '',
    saf_b_address: '',
    saf_b_city: '',
    saf_b_state: '',
    saf_b_landmark: '',
    saf_b_email: '',
  });
  const [anonetCustomerFiles, setAnonetCustomerFiles] = useState([]);
  const [anonetDefaultRecipients, setAnonetDefaultRecipients] = useState({ to: [], cc: [] });

  const [arcPreview, setArcPreview] = useState(null);
  
  // Track if we came from a circuit (pre-filled mode)
  const [prefilledCircuit, setPrefilledCircuit] = useState(null);

  // Check for URL params to pre-fill form
  useEffect(() => {
    const customer = searchParams.get('customer');
    const circuit = searchParams.get('circuit');
    const bandwidth = searchParams.get('bandwidth');
    
    if (circuit) {
      // Fetch circuit details to get customer info
      const fetchCircuitDetails = async () => {
        try {
          const response = await axios.get(`${API}/circuits/${circuit}`);
          const circuitData = response.data;
          setPrefilledCircuit(circuitData);
          setFormData(prev => ({
            ...prev,
            customer_id: circuitData.customer_id || customer || '',
            circuit_id: circuit,
            bandwidth_mbps: bandwidth ? bandwidth.replace(/[^0-9]/g, '') : ''
          }));
          setCreateDialog(true);
        } catch (error) {
          console.error('Failed to fetch circuit details');
          // Fallback to using params
          setFormData(prev => ({
            ...prev,
            customer_id: customer || '',
            circuit_id: circuit,
            bandwidth_mbps: bandwidth ? bandwidth.replace(/[^0-9]/g, '') : ''
          }));
          setCreateDialog(true);
        }
      };
      fetchCircuitDetails();
      setSearchParams({});
    } else if (customer || bandwidth) {
      setFormData(prev => ({
        ...prev,
        customer_id: customer || '',
        bandwidth_mbps: bandwidth ? bandwidth.replace(/[^0-9]/g, '') : ''
      }));
      setCreateDialog(true);
      setSearchParams({});
    }
  }, [searchParams, API]);

  // Fetch all data
  const fetchData = async () => {
    try {
      const [posRes, customersRes, dcRes, settingsRes, anonetBtsRes, anonetEmailRes] = await Promise.all([
        axios.get(`${API}/vendor-po`),
        axios.get(`${API}/customers`),
        axios.get(`${API}/vendor-po/data-centers`),
        axios.get(`${API}/vendor-po/settings`).catch(() => ({ data: { vendor_emails: {} } })),
        axios.get(`${API}/vendor-po/anonet/bts-locations`).catch(() => ({ data: {} })),
        axios.get(`${API}/vendor-po/anonet/email-defaults`).catch(() => ({ data: { to: [], cc: [] } })),
      ]);
      setPurchaseOrders(posRes.data);
      setCustomers(customersRes.data);
      setDataCenters(dcRes.data);
      setVendorSettings(settingsRes.data);
      setAnonetBtsLocations(anonetBtsRes.data || {});
      setAnonetDefaultRecipients(anonetEmailRes.data || { to: [], cc: [] });
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  // Fetch circuits when customer changes
  const fetchCircuits = async (customerId) => {
    if (!customerId) {
      setCircuits([]);
      return;
    }
    try {
      const response = await axios.get(`${API}/circuits?customer_id=${customerId}`);
      setCircuits(response.data);
    } catch (error) {
      console.error('Failed to fetch circuits');
    }
  };

  // Calculate ARC preview when bandwidth changes (vendor-aware)
  const calculateArcPreview = async (bandwidth) => {
    if (!bandwidth || bandwidth <= 0) {
      setArcPreview(null);
      return;
    }
    try {
      const vendor = formData.vendor || 'GTPL';
      const response = await axios.get(`${API}/vendor-po/calculate-arc?bandwidth_mbps=${bandwidth}&vendor=${encodeURIComponent(vendor)}`);
      setArcPreview(response.data);
    } catch (error) {
      console.error('Failed to calculate ARC');
    }
  };

  useEffect(() => {
    fetchData();
  }, [API]);

  useEffect(() => {
    if (formData.customer_id) {
      fetchCircuits(formData.customer_id);
    }
  }, [formData.customer_id]);

  useEffect(() => {
    if (formData.bandwidth_mbps) {
      calculateArcPreview(parseFloat(formData.bandwidth_mbps));
    }
  }, [formData.bandwidth_mbps, formData.vendor]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.customer_id || !formData.circuit_id || !formData.bandwidth_mbps || !formData.end_a_location) {
      toast.error('Please fill all required fields');
      return;
    }

    // Check if manual ARC is needed
    if (arcPreview?.requires_manual && !formData.manual_arc) {
      toast.error('Bandwidth exceeds threshold. Please enter manual ARC.');
      return;
    }

    try {
      const payload = {
        vendor: formData.vendor || 'GTPL',
        customer_id: formData.customer_id,
        circuit_id: formData.circuit_id,
        bandwidth_mbps: parseFloat(formData.bandwidth_mbps),
        end_a_location: formData.end_a_location,
        po_date: format(formData.po_date, 'dd/MM/yyyy')
      };
      if (formData.manual_arc) {
        payload.manual_arc = parseFloat(formData.manual_arc);
      }
      if (formData.vendor === 'Anonet') {
        if (formData.attention_to) payload.attention_to = formData.attention_to;
        payload.contract_period_months = parseInt(formData.contract_period_months || 12);
        payload.delivery_timeline = formData.delivery_timeline || '3-4 Weeks';
        payload.static_ip = formData.static_ip || null;
        payload.drop_interface = formData.drop_interface || null;
        payload.bgp_required = formData.bgp_required || null;
      }

      await axios.post(`${API}/vendor-po`, payload);
      toast.success('Purchase order created successfully');
      setCreateDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create purchase order');
    }
  };

  const resetForm = () => {
    setFormData({
      vendor: 'GTPL',
      customer_id: '',
      circuit_id: '',
      bandwidth_mbps: '',
      end_a_location: '',
      manual_arc: '',
      po_date: new Date(),
      attention_to: '',
      contract_period_months: 12,
      delivery_timeline: '3-4 Weeks',
      static_ip: '',
      drop_interface: '',
      bgp_required: '',
    });
    setCircuits([]);
    setArcPreview(null);
    setPrefilledCircuit(null);
  };

  // Handle PO Number Edit (Admin only)
  const handleEditPONumber = async () => {
    if (!editPONumberDialog.newPONumber.trim()) {
      toast.error('Please enter a valid PO number');
      return;
    }
    try {
      await axios.put(`${API}/vendor-po/${editPONumberDialog.po.po_id}`, {
        po_number: editPONumberDialog.newPONumber.trim()
      });
      toast.success('PO number updated successfully');
      setEditPONumberDialog({ open: false, po: null, newPONumber: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update PO number');
    }
  };

  // Handle Status Update
  const handleUpdateStatus = async () => {
    if (!updateStatusDialog.newStatus) {
      toast.error('Please select a status');
      return;
    }
    if (updateStatusDialog.newStatus === 'constraint' && !updateStatusDialog.reason.trim()) {
      toast.error('Please provide a reason for constraint');
      return;
    }
    try {
      const payload = {
        status: updateStatusDialog.newStatus,
        reason: updateStatusDialog.reason
      };
      if (updateStatusDialog.newStatus === 'ra_generated' && updateStatusDialog.ra_number.trim()) {
        payload.ra_number = updateStatusDialog.ra_number.trim();
      }
      await axios.post(`${API}/vendor-po/${updateStatusDialog.po.po_id}/update-status`, payload);
      toast.success(`Status updated to ${STATUS_LABELS[updateStatusDialog.newStatus]}`);
      setUpdateStatusDialog({ open: false, po: null, newStatus: '', reason: '', ra_number: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  // Handle RA Number update (inline)
  const handleUpdateRANumber = async (poId, raNumber) => {
    if (!raNumber.trim()) { toast.error('Enter RA number'); return; }
    try {
      await axios.post(`${API}/vendor-po/${poId}/update-ra-number`, { ra_number: raNumber.trim() });
      toast.success('RA number updated');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update RA number');
    }
  };

  // Open the Remap dialog pre-filled with the PO's current customer
  const handleOpenRemap = (po) => {
    setRemapDialog({ open: true, po, customer_id: po.customer_id || '', circuit_id: '', submitting: false });
    setRemapCircuits([]);
    if (po.customer_id) {
      setRemapCircuitsLoading(true);
      axios.get(`${API}/circuits?customer_id=${po.customer_id}`)
        .then(r => setRemapCircuits(r.data || []))
        .catch(() => setRemapCircuits([]))
        .finally(() => setRemapCircuitsLoading(false));
    }
  };

  // When customer changes inside the Remap dialog, refresh the circuit list
  const handleRemapCustomerChange = async (customerId) => {
    setRemapDialog(prev => ({ ...prev, customer_id: customerId, circuit_id: '' }));
    setRemapCircuits([]);
    if (!customerId) return;
    setRemapCircuitsLoading(true);
    try {
      const r = await axios.get(`${API}/circuits?customer_id=${customerId}`);
      setRemapCircuits(r.data || []);
    } catch (e) {
      setRemapCircuits([]);
    } finally {
      setRemapCircuitsLoading(false);
    }
  };

  const submitRemap = async () => {
    const { po, customer_id, circuit_id } = remapDialog;
    if (!po) return;
    if (!customer_id || !circuit_id) {
      toast.error('Pick both a customer and a circuit');
      return;
    }
    setRemapDialog(prev => ({ ...prev, submitting: true }));
    try {
      await axios.post(`${API}/vendor-po/${po.po_id}/remap`, { customer_id, circuit_id });
      toast.success('PO remapped');
      setRemapDialog({ open: false, po: null, customer_id: '', circuit_id: '', submitting: false });
      setRemapCircuits([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remap PO');
      setRemapDialog(prev => ({ ...prev, submitting: false }));
    }
  };

  const handleDownloadPDF = async (poId) => {
    try {
      const response = await axios.get(`${API}/vendor-po/${poId}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `PO_${poId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const handleApprove = async (poId) => {
    if (!window.confirm('Approve this purchase order?')) return;
    try {
      await axios.post(`${API}/vendor-po/${poId}/approve`);
      toast.success('Purchase order approved');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleSendEmail = async (poId) => {
    if (!window.confirm('Send this purchase order via email to the vendor?')) return;
    try {
      const response = await axios.post(`${API}/vendor-po/${poId}/send`);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    }
  };

  const handleOpenAnonetSend = async (po) => {
    let cust = {};
    let circuit = {};
    try {
      [cust, circuit] = await Promise.all([
        axios.get(`${API}/customers/${po.customer_id}`).then(r => r.data).catch(() => ({})),
        po.circuit_id ? axios.get(`${API}/circuits/${po.circuit_id}`).then(r => r.data).catch(() => ({})) : Promise.resolve({}),
      ]);
    } catch (e) { /* ignore */ }
    // Load any pre-uploaded files for the customer
    try {
      const r = await axios.get(`${API}/vendor-po/${po.po_id}/customer-files`);
      setAnonetCustomerFiles(r.data || []);
    } catch (e) {
      setAnonetCustomerFiles([]);
    }
    setAnonetSendDialog({
      open: true, po, submitting: false,
      to_str: (anonetDefaultRecipients.to || []).join(', '),
      cc_str: (anonetDefaultRecipients.cc || []).join(', '),
      latitude: circuit.latitude ?? '',
      longitude: circuit.longitude ?? '',
      address_proof_file_id: '',
      address_proof_filename: '',
      address_proof_b64: '',
      address_proof_mime: '',
      saf_purpose: 'NLD',
      saf_b_contact_name: cust.contact_person || cust.contact_name || '',
      saf_b_dept_name: cust.company_name || '',
      saf_b_address: circuit.installation_address || po.end_b_address || '',
      saf_b_city: circuit.city || '',
      saf_b_state: circuit.state || '',
      saf_b_landmark: circuit.landmark || '',
      saf_b_email: cust.email || '',
    });
  };

  const handleAnonetAddressProofUpload = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('File too large (max 10MB)'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const b64 = result.split(',')[1] || result;
      setAnonetSendDialog(prev => ({
        ...prev,
        address_proof_file_id: '',
        address_proof_filename: file.name,
        address_proof_b64: b64,
        address_proof_mime: file.type || 'application/octet-stream',
      }));
    };
    reader.readAsDataURL(file);
  };

  const submitAnonetSend = async () => {
    const d = anonetSendDialog;
    if (!d.po) return;
    const splitEmails = (s) => (s || '').split(/[,;\n]/).map(e => e.trim()).filter(e => e && e.includes('@'));
    const to = splitEmails(d.to_str);
    const cc = splitEmails(d.cc_str);
    if (!to.length) { toast.error('At least one To recipient is required'); return; }

    const payload = {
      to, cc,
      latitude: d.latitude || null,
      longitude: d.longitude || null,
      saf_data: {
        purpose: d.saf_purpose,
        b_contact_name: d.saf_b_contact_name,
        b_dept_name: d.saf_b_dept_name,
        b_address: d.saf_b_address,
        b_city: d.saf_b_city,
        b_state: d.saf_b_state,
        b_landmark: d.saf_b_landmark,
        b_email: d.saf_b_email,
      }
    };
    if (d.address_proof_file_id) {
      payload.address_proof = { file_id: d.address_proof_file_id };
    } else if (d.address_proof_b64) {
      payload.address_proof = {
        filename: d.address_proof_filename,
        content_base64: d.address_proof_b64,
        mime_type: d.address_proof_mime,
      };
    }
    setAnonetSendDialog(prev => ({ ...prev, submitting: true }));
    try {
      await axios.post(`${API}/vendor-po/${d.po.po_id}/send-anonet`, payload);
      toast.success(`Anonet PO + SAF email sent to ${to.length} recipient(s)`);
      setAnonetSendDialog(prev => ({ ...prev, open: false, submitting: false }));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send Anonet PO email');
      setAnonetSendDialog(prev => ({ ...prev, submitting: false }));
    }
  };

  const handleDownloadSaf = async (poId) => {
    try {
      const response = await axios.get(`${API}/vendor-po/${poId}/saf-pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `SAF_${poId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('SAF downloaded');
    } catch (e) {
      toast.error('Failed to download SAF');
    }
  };

  const handleSendAcceptance = (po) => {
    setAcceptanceDialog({
      open: true,
      po,
      mode: 'send',
      acceptance_date: todayYMD(),
      to_str: (defaultRecipients.to || []).join(', '),
      cc_str: (defaultRecipients.cc || []).join(', '),
      submitting: false,
    });
  };

  const handleMarkDeliveredAndSend = (po) => {
    setAcceptanceDialog({
      open: true,
      po,
      mode: 'mark_send',
      acceptance_date: todayYMD(),
      to_str: (defaultRecipients.to || []).join(', '),
      cc_str: (defaultRecipients.cc || []).join(', '),
      submitting: false,
    });
  };

  const submitAcceptanceEmail = async () => {
    const { po, mode, acceptance_date, to_str, cc_str } = acceptanceDialog;
    if (!po) return;
    if (!acceptance_date) {
      toast.error('Please pick an acceptance date');
      return;
    }
    const splitEmails = (s) => (s || '')
      .split(/[,;\n]/)
      .map(e => e.trim())
      .filter(e => e && e.includes('@'));
    const to = splitEmails(to_str);
    const cc = splitEmails(cc_str);
    if (to.length === 0) {
      toast.error('At least one "To" recipient is required');
      return;
    }
    setAcceptanceDialog(prev => ({ ...prev, submitting: true }));
    try {
      const url = mode === 'mark_send'
        ? `${API}/vendor-po/${po.po_id}/mark-delivered-and-send-acceptance`
        : `${API}/vendor-po/${po.po_id}/send-acceptance-email`;
      const response = await axios.post(url, { acceptance_date, to, cc });
      const verb = mode === 'mark_send' ? 'PO marked Delivered. Acceptance' : 'Acceptance';
      toast.success(`${verb} email sent to ${to.length} recipient(s) (Effective: ${response.data.acceptance_date})`);
      setAcceptanceDialog({ open: false, po: null, mode: 'send', acceptance_date: todayYMD(), to_str: '', cc_str: '', submitting: false });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send acceptance email');
      setAcceptanceDialog(prev => ({ ...prev, submitting: false }));
    }
  };

  const handleDelete = async (poId) => {
    if (!window.confirm('Delete this purchase order? This action cannot be undone.')) return;
    try {
      await axios.delete(`${API}/vendor-po/${poId}`);
      toast.success('Purchase order deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleSaveSettings = async () => {
    try {
      await axios.put(`${API}/vendor-po/settings`, vendorSettings);
      toast.success('Settings saved');
      setSettingsDialog(false);
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  // Open the Settings dialog and load Anonet settings into editable state
  const openSettingsDialog = async () => {
    setSettingsDialog(true);
    setAnonetSettingsLoading(true);
    try {
      const [btsRes, slabsRes, emailRes] = await Promise.all([
        axios.get(`${API}/vendor-po/anonet/bts-locations`).catch(() => ({ data: {} })),
        axios.get(`${API}/vendor-po/anonet/price-slabs`).catch(() => ({ data: [] })),
        axios.get(`${API}/vendor-po/anonet/email-defaults`).catch(() => ({ data: { to: [], cc: [] } })),
      ]);
      // Convert bts dict → array with stable _id so React key isn't tied to the editable "key" field
      const btsArr = Object.entries(btsRes.data || {}).map(([k, v], idx) => ({
        _id: `bts_${idx}_${Math.random().toString(36).slice(2, 8)}`,
        key: k,
        code: v?.code || '',
        address: v?.address || '',
      }));
      setAnonetSettings({
        bts_locations: btsArr,
        price_slabs: Array.isArray(slabsRes.data) ? slabsRes.data : [],
        emails_to: (emailRes.data.to || []).join(', '),
        emails_cc: (emailRes.data.cc || []).join(', '),
      });
    } finally {
      setAnonetSettingsLoading(false);
    }
  };

  const handleSaveAnonetSettings = async () => {
    const splitEmails = (s) => (s || '').split(/[,;\n]/).map(e => e.trim()).filter(Boolean);
    // Serialize bts array → dict (key trimmed; skip empty keys; last write wins on duplicate)
    const btsDict = {};
    for (const row of (anonetSettings.bts_locations || [])) {
      const k = (row.key || '').trim();
      if (!k) continue;
      btsDict[k] = { code: row.code || '', address: row.address || '' };
    }
    try {
      // Save Anonet email defaults (admin)
      await axios.put(`${API}/vendor-po/anonet/email-defaults`, {
        to: splitEmails(anonetSettings.emails_to),
        cc: splitEmails(anonetSettings.emails_cc),
      });
      // Save Anonet BTS locations (admin)
      await axios.put(`${API}/vendor-po/anonet/bts-locations`, btsDict);
      // Save Anonet price slabs (admin)
      await axios.put(`${API}/vendor-po/anonet/price-slabs`, anonetSettings.price_slabs);
      toast.success('Anonet settings saved');
      // Refresh in-memory state for the create dialog
      const [btsRes, emailRes] = await Promise.all([
        axios.get(`${API}/vendor-po/anonet/bts-locations`),
        axios.get(`${API}/vendor-po/anonet/email-defaults`),
      ]);
      setAnonetBtsLocations(btsRes.data || {});
      setAnonetDefaultRecipients(emailRes.data || { to: [], cc: [] });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save Anonet settings');
    }
  };

  // BTS location editors (array-of-rows model with stable _id)
  const addAnonetBtsRow = () => {
    setAnonetSettings(prev => ({
      ...prev,
      bts_locations: [
        ...(prev.bts_locations || []),
        { _id: `bts_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`, key: '', code: '', address: '' },
      ],
    }));
  };
  const updateAnonetBtsRow = (rowId, patch) => {
    setAnonetSettings(prev => ({
      ...prev,
      bts_locations: (prev.bts_locations || []).map(r => r._id === rowId ? { ...r, ...patch } : r),
    }));
  };
  const removeAnonetBtsRow = (rowId) => {
    setAnonetSettings(prev => ({
      ...prev,
      bts_locations: (prev.bts_locations || []).filter(r => r._id !== rowId),
    }));
  };

  // Price slab editors
  const addAnonetSlab = () => {
    setAnonetSettings(prev => ({
      ...prev,
      price_slabs: [...prev.price_slabs, { min_bandwidth: 0, max_bandwidth: 0, rate_per_mbps: 0, manual_required: false }]
    }));
  };
  const updateAnonetSlab = (i, patch) => {
    setAnonetSettings(prev => {
      const next = [...prev.price_slabs];
      next[i] = { ...next[i], ...patch };
      return { ...prev, price_slabs: next };
    });
  };
  const removeAnonetSlab = (i) => {
    setAnonetSettings(prev => ({ ...prev, price_slabs: prev.price_slabs.filter((_, idx) => idx !== i) }));
  };

  // Vendor-scoped POs (used by stats so the dashboard reflects the active tab)
  const vendorScopedPOs = purchaseOrders.filter(po => {
    const poVendor = (po.vendor || 'GTPL').toUpperCase();
    return vendorFilter === 'all' || poVendor === vendorFilter;
  });

  // Filtered POs (vendor + status + search) — used by the table below
  const filteredPOs = vendorScopedPOs.filter(po => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch =
      po.po_number?.toLowerCase().includes(searchLower) ||
      po.customer_name?.toLowerCase().includes(searchLower) ||
      po.end_b_address?.toLowerCase().includes(searchLower);
    const matchesStatus = statusFilter === 'all' || po.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Vendor counts (always over the full list, not filtered)
  const vendorCount = (v) => purchaseOrders.filter(p => (p.vendor || 'GTPL').toUpperCase() === v).length;
  const counts = { all: purchaseOrders.length, GTPL: vendorCount('GTPL'), ANONET: vendorCount('ANONET') };

  // Stats counts respect the active vendor tab
  const statusCount = (s) => vendorScopedPOs.filter(p => p.status === s).length;

  // Mobile PO Card
  const POCard = ({ po }) => (
    <div className="bg-white border rounded-lg p-4 space-y-3">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className="font-mono font-medium text-slate-900 text-sm">{po.po_number}</p>
          <p className="text-sm text-slate-600 truncate">
            <a href={`/customers/${po.customer_id}`} onClick={(e) => { e.preventDefault(); navigate(`/customers/${po.customer_id}`); }} className="text-indigo-600 hover:text-indigo-800 hover:underline">
              {po.customer_name}
            </a>
          </p>
        </div>
        <Badge className={STATUS_COLORS[po.status]}>
          {STATUS_LABELS[po.status]}
        </Badge>
      </div>
      
      <div className="flex items-center justify-between text-sm">
        <span className="text-slate-600">{parseInt(po.bandwidth_mbps)} Mbps</span>
        {!isDeliveryOnly && <span className="font-medium text-emerald-600">₹{po.arc?.toLocaleString()}</span>}
      </div>
      
      {po.ra_number && (
        <div className="text-xs text-purple-600 font-medium">RA: {po.ra_number}</div>
      )}
      {!po.ra_number && po.status === 'ra_generated' && (
        <RANumberInput poId={po.po_id} onSave={handleUpdateRANumber} />
      )}
      
      <div className="text-xs text-slate-500">
        Date: {po.po_date}
      </div>
      
      <div className="flex flex-wrap gap-2 pt-2 border-t">
        <Button size="sm" variant="outline" onClick={() => setViewDialog({ open: true, po })} className="flex-1">
          <Eye className="h-4 w-4 mr-1" /> View
        </Button>
        <Button size="sm" variant="outline" onClick={() => handleDownloadPDF(po.po_id)} className="flex-1">
          <Download className="h-4 w-4 mr-1" /> PDF
        </Button>
        {po.status === 'draft' && isAdmin && (
          <Button size="sm" variant="outline" className="text-emerald-600 flex-1" onClick={() => handleApprove(po.po_id)}>
            <CheckCircle className="h-4 w-4 mr-1" /> Approve
          </Button>
        )}
        {po.status === 'approved' && (
          <Button size="sm" variant="outline" className="text-blue-600 flex-1" onClick={() => handleSendEmail(po.po_id)}>
            <Send className="h-4 w-4 mr-1" /> Send
          </Button>
        )}
        {po.status === 'delivered' && (po.vendor || '').toUpperCase() === 'GTPL' && (
          <Button
            size="sm"
            variant="outline"
            className={po.acceptance_email_status === 'sent' ? 'text-emerald-600 flex-1' : 'text-indigo-600 flex-1'}
            onClick={() => handleSendAcceptance(po)}
            data-testid={`send-acceptance-${po.po_id}`}
            title={po.acceptance_email_sent_at ? `Last sent: ${new Date(po.acceptance_email_sent_at).toLocaleString()}` : 'Send GTPL acceptance email'}
          >
            <Send className="h-4 w-4 mr-1" />
            {po.acceptance_email_status === 'sent' ? 'Resend Acceptance' : 'Send Acceptance'}
          </Button>
        )}
        {po.status !== 'delivered' && po.circuit_is_delivered && (po.vendor || '').toUpperCase() === 'GTPL' && (
          <Button
            size="sm"
            variant="outline"
            className="text-amber-700 flex-1 border-amber-300 hover:bg-amber-50"
            onClick={() => handleMarkDeliveredAndSend(po)}
            data-testid={`mark-delivered-send-${po.po_id}`}
            title="Circuit is already delivered. Click to mark this PO Delivered and send the acceptance email."
          >
            <CheckCircle className="h-4 w-4 mr-1" /> Mark Delivered & Send
          </Button>
        )}
        {(po.vendor || '').toLowerCase() === 'anonet' && ['approved', 'sent', 'acknowledged', 'provider_processed', 'ra_generated', 'ready_for_delivery', 'delivered'].includes(po.status) && (
          <Button
            size="sm"
            variant="outline"
            className={po.saf_email_sent_at ? 'text-emerald-700 flex-1 border-emerald-300' : 'text-purple-700 flex-1 border-purple-300'}
            onClick={() => handleOpenAnonetSend(po)}
            data-testid={`anonet-send-${po.po_id}`}
            title={po.saf_email_sent_at ? `Last sent: ${new Date(po.saf_email_sent_at).toLocaleString()}` : 'Send Anonet PO + SAF email'}
          >
            <Send className="h-4 w-4 mr-1" /> {po.saf_email_sent_at ? 'Resend PO+SAF' : 'Send PO+SAF'}
          </Button>
        )}
        {(po.vendor || '').toLowerCase() === 'anonet' && (
          <Button
            size="sm"
            variant="outline"
            className="text-purple-600 flex-1"
            onClick={() => handleDownloadSaf(po.po_id)}
            data-testid={`download-saf-${po.po_id}`}
          >
            <FileText className="h-4 w-4 mr-1" /> SAF
          </Button>
        )}
        {isAdmin && (
          <Button size="sm" variant="outline" className="text-amber-700 border-amber-300 hover:bg-amber-50 flex-1" onClick={() => handleOpenRemap(po)} data-testid={`remap-po-card-${po.po_id}`}>
            <Link2 className="h-4 w-4 mr-1" /> Remap
          </Button>
        )}
        {isAdmin && (
          <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDelete(po.po_id)}>
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <div className="space-y-4 lg:space-y-6" data-testid="purchase-orders-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl lg:text-3xl font-semibold text-slate-900 tracking-tight">Purchase Orders</h2>
          <p className="text-sm lg:text-base text-slate-600 mt-1">Manage vendor purchase orders</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          {isAdmin && (
            <Button variant="outline" onClick={openSettingsDialog} className="w-full sm:w-auto" data-testid="vendor-settings-btn">
              Settings
            </Button>
          )}
          {canCreatePO && (
            <Button onClick={() => setCreateDialog(true)} className="bg-slate-900 hover:bg-slate-800 w-full sm:w-auto" data-testid="create-po-btn">
              <Plus className="mr-2 h-4 w-4" /> Create PO
            </Button>
          )}
        </div>
      </div>

      {/* Vendor tabs (ANONET / GTPL) */}
      <Tabs value={vendorFilter} onValueChange={setVendorFilter}>
        <TabsList className="grid w-full grid-cols-3 max-w-md">
          <TabsTrigger value="all" data-testid="vendor-tab-all">
            All
            <span className="ml-2 inline-flex items-center justify-center rounded-full bg-slate-200 px-2 py-0.5 text-xs font-semibold text-slate-700">{counts.all}</span>
          </TabsTrigger>
          <TabsTrigger value="GTPL" data-testid="vendor-tab-gtpl">
            GTPL
            <span className="ml-2 inline-flex items-center justify-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-700">{counts.GTPL}</span>
          </TabsTrigger>
          <TabsTrigger value="ANONET" data-testid="vendor-tab-anonet">
            Anonet
            <span className="ml-2 inline-flex items-center justify-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">{counts.ANONET}</span>
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Filters */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search by PO number, customer, or END B address..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-po-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48" data-testid="status-filter">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="pending_approval">Pending Approval</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="acknowledged">Acknowledged</SelectItem>
                <SelectItem value="provider_processed">Provider Processed</SelectItem>
                <SelectItem value="ra_generated">RA Generated</SelectItem>
                <SelectItem value="constraint">Constraint</SelectItem>
                <SelectItem value="ready_for_delivery">Ready for Delivery</SelectItem>
                <SelectItem value="delivered">Delivered</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Stats — respect the active vendor tab */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setStatusFilter('all')}>
          <CardContent className="py-3">
            <p className="text-xs text-slate-500">Total POs{vendorFilter !== 'all' ? ` (${vendorFilter})` : ''}</p>
            <p className="text-xl font-bold text-slate-900" data-testid="po-stat-total">{vendorScopedPOs.length}</p>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer hover:shadow-md transition-shadow ${statusFilter === 'draft' ? 'ring-2 ring-amber-400' : ''}`} onClick={() => setStatusFilter('draft')}>
          <CardContent className="py-3">
            <p className="text-xs text-slate-500">Draft</p>
            <p className="text-xl font-bold text-amber-600" data-testid="po-stat-draft">{statusCount('draft')}</p>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer hover:shadow-md transition-shadow ${statusFilter === 'approved' ? 'ring-2 ring-emerald-400' : ''}`} onClick={() => setStatusFilter('approved')}>
          <CardContent className="py-3">
            <p className="text-xs text-slate-500">Approved</p>
            <p className="text-xl font-bold text-emerald-600" data-testid="po-stat-approved">{statusCount('approved')}</p>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer hover:shadow-md transition-shadow ${statusFilter === 'sent' ? 'ring-2 ring-blue-400' : ''}`} onClick={() => setStatusFilter('sent')}>
          <CardContent className="py-3">
            <p className="text-xs text-slate-500">Sent</p>
            <p className="text-xl font-bold text-blue-600" data-testid="po-stat-sent">{statusCount('sent')}</p>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer hover:shadow-md transition-shadow ${statusFilter === 'provider_processed' ? 'ring-2 ring-cyan-400' : ''}`} onClick={() => setStatusFilter('provider_processed')}>
          <CardContent className="py-3">
            <p className="text-xs text-slate-500">Provider Processed</p>
            <p className="text-xl font-bold text-cyan-600" data-testid="po-stat-provider-processed">{statusCount('provider_processed')}</p>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer hover:shadow-md transition-shadow ${statusFilter === 'ra_generated' ? 'ring-2 ring-purple-400' : ''}`} onClick={() => setStatusFilter('ra_generated')}>
          <CardContent className="py-3">
            <p className="text-xs text-slate-500">RA Generated</p>
            <p className="text-xl font-bold text-purple-600" data-testid="po-stat-ra-generated">{statusCount('ra_generated')}</p>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer hover:shadow-md transition-shadow ${statusFilter === 'constraint' ? 'ring-2 ring-red-400' : ''}`} onClick={() => setStatusFilter('constraint')}>
          <CardContent className="py-3">
            <p className="text-xs text-slate-500">Constraint</p>
            <p className="text-xl font-bold text-red-600" data-testid="po-stat-constraint">{statusCount('constraint')}</p>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer hover:shadow-md transition-shadow ${statusFilter === 'ready_for_delivery' ? 'ring-2 ring-green-400' : ''}`} onClick={() => setStatusFilter('ready_for_delivery')}>
          <CardContent className="py-3">
            <p className="text-xs text-slate-500">Ready for Delivery</p>
            <p className="text-xl font-bold text-green-600" data-testid="po-stat-ready">{statusCount('ready_for_delivery')}</p>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer hover:shadow-md transition-shadow ${statusFilter === 'acknowledged' ? 'ring-2 ring-indigo-400' : ''}`} onClick={() => setStatusFilter('acknowledged')}>
          <CardContent className="py-3">
            <p className="text-xs text-slate-500">Acknowledged</p>
            <p className="text-xl font-bold text-indigo-600" data-testid="po-stat-acknowledged">{statusCount('acknowledged')}</p>
          </CardContent>
        </Card>
      </div>

      {/* PO List */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg lg:text-xl">
            <ShoppingCart className="h-5 w-5" />
            Purchase Orders
          </CardTitle>
          <CardDescription>
            {filteredPOs.length} purchase order{filteredPOs.length !== 1 ? 's' : ''} found
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0 lg:p-6 lg:pt-0">
          {filteredPOs.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <FileText className="mx-auto h-12 w-12 text-slate-300 mb-2" />
              <p>No purchase orders found</p>
            </div>
          ) : (
            <>
              {/* Mobile View - Cards */}
              <div className="lg:hidden p-4 space-y-3">
                {filteredPOs.map((po) => (
                  <POCard key={po.po_id} po={po} />
                ))}
              </div>

              {/* Desktop View - Table */}
              <div className="hidden lg:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>PO Number</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Bandwidth</TableHead>
                      {!isDeliveryOnly && <TableHead>ARC</TableHead>}
                      <TableHead>Status</TableHead>
                      <TableHead>RA Number</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredPOs.map((po) => (
                      <TableRow key={po.po_id} data-testid={`po-row-${po.po_id}`}>
                        <TableCell className="font-mono font-medium">{po.po_number}</TableCell>
                        <TableCell>
                          <a href={`/customers/${po.customer_id}`} onClick={(e) => { e.preventDefault(); navigate(`/customers/${po.customer_id}`); }} className="text-indigo-600 hover:text-indigo-800 hover:underline">
                            {po.customer_name}
                          </a>
                        </TableCell>
                        <TableCell>{parseInt(po.bandwidth_mbps)} Mbps</TableCell>
                        {!isDeliveryOnly && <TableCell className="font-medium">₹{po.arc?.toLocaleString()}</TableCell>}
                        <TableCell>
                          <Badge className={STATUS_COLORS[po.status]}>
                            {STATUS_LABELS[po.status]}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {po.ra_number ? (
                            <span className="text-purple-700 font-medium text-sm">{po.ra_number}</span>
                          ) : po.status === 'ra_generated' ? (
                            <RANumberInput poId={po.po_id} onSave={handleUpdateRANumber} />
                          ) : (
                            <span className="text-slate-300">-</span>
                          )}
                        </TableCell>
                        <TableCell>{po.po_date}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2 flex-wrap">
                            <Button size="sm" variant="outline" onClick={() => setViewDialog({ open: true, po })} data-testid={`view-po-${po.po_id}`}>
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleDownloadPDF(po.po_id)} data-testid={`download-po-${po.po_id}`}>
                              <Download className="h-4 w-4" />
                            </Button>
                            {canEditPONumber && (
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="text-slate-600"
                                onClick={() => setEditPONumberDialog({ open: true, po, newPONumber: po.po_number })}
                                title="Edit PO Number"
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                            )}
                            {isAdmin && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-amber-700 border-amber-300 hover:bg-amber-50"
                                onClick={() => handleOpenRemap(po)}
                                title="Remap to different customer / circuit"
                                data-testid={`remap-po-${po.po_id}`}
                              >
                                <Link2 className="h-4 w-4" />
                              </Button>
                            )}
                            {canUpdateStatus && po.status !== 'draft' && (
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="text-purple-600"
                                onClick={() => setUpdateStatusDialog({ open: true, po, newStatus: '', reason: '', ra_number: '' })}
                                title="Update Status"
                              >
                                <RefreshCw className="h-4 w-4" />
                              </Button>
                            )}
                            {po.status === 'draft' && isAdmin && (
                              <Button size="sm" variant="outline" className="text-emerald-600" onClick={() => handleApprove(po.po_id)} data-testid={`approve-po-${po.po_id}`}>
                                <CheckCircle className="h-4 w-4" />
                              </Button>
                            )}
                            {po.status === 'approved' && (
                              <Button size="sm" variant="outline" className="text-blue-600" onClick={() => handleSendEmail(po.po_id)} data-testid={`send-po-${po.po_id}`}>
                                <Send className="h-4 w-4" />
                              </Button>
                            )}
                            {po.status === 'delivered' && (po.vendor || '').toUpperCase() === 'GTPL' && (
                              <Button
                                size="sm"
                                variant="outline"
                                className={po.acceptance_email_status === 'sent' ? 'text-emerald-600' : 'text-indigo-600'}
                                onClick={() => handleSendAcceptance(po)}
                                data-testid={`send-acceptance-table-${po.po_id}`}
                                title={po.acceptance_email_sent_at ? `Last sent: ${new Date(po.acceptance_email_sent_at).toLocaleString()}\nClick to resend` : 'Send GTPL acceptance email'}
                              >
                                <Send className="h-4 w-4" />
                              </Button>
                            )}
                            {po.status !== 'delivered' && po.circuit_is_delivered && (po.vendor || '').toUpperCase() === 'GTPL' && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-amber-700 border-amber-300 hover:bg-amber-50"
                                onClick={() => handleMarkDeliveredAndSend(po)}
                                data-testid={`mark-delivered-send-table-${po.po_id}`}
                                title="Circuit is already delivered. Click to mark PO Delivered and send acceptance email."
                              >
                                <CheckCircle className="h-4 w-4" />
                              </Button>
                            )}
                            {(po.vendor || '').toLowerCase() === 'anonet' && ['approved', 'sent', 'acknowledged', 'provider_processed', 'ra_generated', 'ready_for_delivery', 'delivered'].includes(po.status) && (
                              <Button
                                size="sm"
                                variant="outline"
                                className={po.saf_email_sent_at ? 'text-emerald-700 border-emerald-300' : 'text-purple-700 border-purple-300'}
                                onClick={() => handleOpenAnonetSend(po)}
                                data-testid={`anonet-send-table-${po.po_id}`}
                                title={po.saf_email_sent_at ? `Last sent: ${new Date(po.saf_email_sent_at).toLocaleString()}` : 'Send Anonet PO + SAF email'}
                              >
                                <Send className="h-4 w-4" />
                              </Button>
                            )}
                            {(po.vendor || '').toLowerCase() === 'anonet' && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-purple-600"
                                onClick={() => handleDownloadSaf(po.po_id)}
                                data-testid={`download-saf-table-${po.po_id}`}
                                title="Download SAF PDF"
                              >
                                <FileText className="h-4 w-4" />
                              </Button>
                            )}
                            {isAdmin && (
                              <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleDelete(po.po_id)} data-testid={`delete-po-${po.po_id}`}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Create PO Dialog */}
      <Dialog open={createDialog} onOpenChange={(open) => { setCreateDialog(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Vendor Purchase Order</DialogTitle>
            <DialogDescription>Generate a new PO for GTPL or Anonet</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            {/* Vendor selector */}
            <div className="space-y-2">
              <Label>Vendor *</Label>
              <Select 
                value={formData.vendor} 
                onValueChange={(v) => setFormData({ ...formData, vendor: v, end_a_location: '' })}
              >
                <SelectTrigger data-testid="select-vendor">
                  <SelectValue placeholder="Select vendor" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="GTPL" data-testid="select-vendor-gtpl">GTPL Broadband</SelectItem>
                  <SelectItem value="Anonet" data-testid="select-vendor-anonet">Anonet Communications</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                {formData.vendor === 'Anonet'
                  ? 'Uses Anonet ADPO format + SAF form. Includes lat/long + address-proof email flow.'
                  : 'Uses GTPL Broadband PO format with letterhead and acceptance email flow.'}
              </p>
            </div>

            {/* Show Circuit info when pre-filled from CustomerDetail */}
            {prefilledCircuit ? (
              <div className="p-4 bg-slate-50 rounded-lg border">
                <p className="text-sm text-slate-500 mb-1">Circuit</p>
                <p className="font-medium text-slate-900">
                  {prefilledCircuit.ant_circuit_id} - {prefilledCircuit.location_name || prefilledCircuit.city}
                </p>
                {prefilledCircuit.installation_address && (
                  <p className="text-sm text-slate-600 mt-1">{prefilledCircuit.installation_address}</p>
                )}
              </div>
            ) : (
              /* Show Customer and Circuit dropdowns when creating from scratch */
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Customer *</Label>
                  <Select 
                    value={formData.customer_id} 
                    onValueChange={(v) => setFormData({ ...formData, customer_id: v, circuit_id: '' })}
                  >
                    <SelectTrigger data-testid="select-customer">
                      <SelectValue placeholder="Select customer" />
                    </SelectTrigger>
                    <SelectContent>
                      {customers.map((c) => (
                        <SelectItem key={c.customer_id} value={c.customer_id}>{c.company_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Circuit *</Label>
                  <Select 
                    value={formData.circuit_id} 
                    onValueChange={(v) => setFormData({ ...formData, circuit_id: v })}
                    disabled={!formData.customer_id}
                  >
                    <SelectTrigger data-testid="select-circuit">
                      <SelectValue placeholder={formData.customer_id ? "Select circuit" : "Select customer first"} />
                    </SelectTrigger>
                    <SelectContent>
                      {circuits.map((c) => (
                        <SelectItem key={c.circuit_id} value={c.circuit_id}>
                          {c.ant_circuit_id} - {c.location_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>PO Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                      data-testid="po-date-picker"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.po_date ? format(formData.po_date, 'dd/MM/yyyy') : 'Select date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.po_date}
                      onSelect={(date) => setFormData({ ...formData, po_date: date || new Date() })}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label>Bandwidth (Mbps) *</Label>
                <Input
                  type="number"
                  value={formData.bandwidth_mbps}
                  onChange={(e) => setFormData({ ...formData, bandwidth_mbps: e.target.value })}
                  placeholder="e.g., 155"
                  data-testid="input-bandwidth"
                />
              </div>
              <div className="space-y-2">
                <Label>{formData.vendor === 'Anonet' ? 'End A BTS Location *' : 'End A Location *'}</Label>
                <Select 
                  value={formData.end_a_location} 
                  onValueChange={(v) => setFormData({ ...formData, end_a_location: v })}
                >
                  <SelectTrigger data-testid="select-end-a">
                    <SelectValue placeholder={formData.vendor === 'Anonet' ? 'Select BTS location' : 'Select data center'} />
                  </SelectTrigger>
                  <SelectContent>
                    {formData.vendor === 'Anonet'
                      ? Object.entries(anonetBtsLocations).map(([key, val]) => (
                          <SelectItem key={key} value={key}>
                            {val.code} — {key.replace(/_/g, ' ')}
                          </SelectItem>
                        ))
                      : Object.entries(dataCenters).map(([key, _address]) => (
                          <SelectItem key={key} value={key}>
                            {key.charAt(0).toUpperCase() + key.slice(1)}
                          </SelectItem>
                        ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* ARC Preview */}
            {arcPreview && (
              <div className={`p-4 rounded-lg border ${arcPreview.requires_manual ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'}`}>
                <div className="flex items-start gap-3">
                  {arcPreview.requires_manual ? (
                    <AlertCircle className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
                  ) : (
                    <CheckCircle className="h-5 w-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className={`font-medium ${arcPreview.requires_manual ? 'text-amber-800' : 'text-emerald-800'}`}>
                      ARC Calculation
                    </p>
                    {arcPreview.requires_manual ? (
                      <p className="text-sm text-amber-700 mt-1">
                        Bandwidth exceeds 2096 Mbps. Please enter manual ARC below.
                      </p>
                    ) : (
                      <div className="text-sm text-emerald-700 mt-1">
                        <p>Rate: ₹{arcPreview.rate_per_mbps}/Mbps/Month</p>
                        <p className="font-semibold">Annual Recurring Cost: ₹{arcPreview.arc?.toLocaleString()}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Manual ARC Input */}
            {arcPreview?.requires_manual && (
              <div className="space-y-2">
                <Label>Manual ARC (Annual) *</Label>
                <Input
                  type="number"
                  value={formData.manual_arc}
                  onChange={(e) => setFormData({ ...formData, manual_arc: e.target.value })}
                  placeholder="Enter annual recurring cost"
                  data-testid="input-manual-arc"
                />
              </div>
            )}

            {/* End A Address Preview */}
            {formData.end_a_location && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">End A Address:</p>
                <p className="text-sm font-medium text-slate-800">
                  {formData.vendor === 'Anonet'
                    ? `${anonetBtsLocations[formData.end_a_location]?.code || ''} — ${anonetBtsLocations[formData.end_a_location]?.address || ''}`
                    : dataCenters[formData.end_a_location]}
                </p>
              </div>
            )}

            {/* Anonet-specific extras */}
            {formData.vendor === 'Anonet' && (
              <div className="space-y-3 p-3 rounded-lg border bg-purple-50/40 border-purple-200">
                <p className="text-sm font-medium text-purple-900">Anonet-specific details</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Kind Attn</Label>
                    <Input
                      value={formData.attention_to}
                      onChange={(e) => setFormData({ ...formData, attention_to: e.target.value })}
                      placeholder="Ajay"
                      data-testid="input-anonet-attention"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Contract Period (months)</Label>
                    <Input
                      type="number"
                      value={formData.contract_period_months}
                      onChange={(e) => setFormData({ ...formData, contract_period_months: e.target.value })}
                      data-testid="input-anonet-contract-months"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Delivery Timeline</Label>
                    <Input
                      value={formData.delivery_timeline}
                      onChange={(e) => setFormData({ ...formData, delivery_timeline: e.target.value })}
                      placeholder="3-4 Weeks"
                      data-testid="input-anonet-delivery"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Static IPs</Label>
                    <Input
                      value={formData.static_ip}
                      onChange={(e) => setFormData({ ...formData, static_ip: e.target.value })}
                      placeholder="e.g. 1 Static IP"
                      data-testid="input-anonet-static-ip"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Drop Interface</Label>
                    <Input
                      value={formData.drop_interface}
                      onChange={(e) => setFormData({ ...formData, drop_interface: e.target.value })}
                      placeholder="e.g. Ethernet"
                      data-testid="input-anonet-drop-interface"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">BGP Required</Label>
                    <Input
                      value={formData.bgp_required}
                      onChange={(e) => setFormData({ ...formData, bgp_required: e.target.value })}
                      placeholder="Yes / No / Full / Default"
                      data-testid="input-anonet-bgp"
                    />
                  </div>
                </div>
              </div>
            )}

            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button type="button" variant="outline" onClick={() => setCreateDialog(false)} className="w-full sm:w-auto">Cancel</Button>
              <Button type="submit" className="w-full sm:w-auto" data-testid="submit-create-po">Create Purchase Order</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View PO Dialog */}
      <Dialog open={viewDialog.open} onOpenChange={(open) => setViewDialog({ ...viewDialog, open })}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Purchase Order Details</DialogTitle>
            <DialogDescription>{viewDialog.po?.po_number}</DialogDescription>
          </DialogHeader>
          {viewDialog.po && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">PO Number</p>
                  <p className="font-mono font-medium text-sm break-all">{viewDialog.po.po_number}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Status</p>
                  <Badge className={STATUS_COLORS[viewDialog.po.status]}>
                    {STATUS_LABELS[viewDialog.po.status]}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Customer</p>
                  <p className="font-medium">{viewDialog.po.customer_name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">PO Date</p>
                  <p className="font-medium">{viewDialog.po.po_date}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Bandwidth</p>
                  <p className="font-medium">{parseInt(viewDialog.po.bandwidth_mbps)} Mbps (1:1)</p>
                </div>
                {!isDeliveryOnly && (
                  <div>
                    <p className="text-sm text-slate-500">Annual Recurring Cost</p>
                    <p className="font-medium text-emerald-600">₹{viewDialog.po.arc?.toLocaleString()}</p>
                  </div>
                )}
                {viewDialog.po.ra_number && (
                  <div>
                    <p className="text-sm text-slate-500">RA Number</p>
                    <p className="font-medium text-purple-600">{viewDialog.po.ra_number}</p>
                  </div>
                )}
              </div>
              <div className="border-t pt-4">
                <p className="text-sm text-slate-500 mb-2">End A Address (Data Center)</p>
                <p className="text-sm bg-slate-50 p-3 rounded">{viewDialog.po.end_a_address}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500 mb-2">End B Address (Installation)</p>
                <p className="text-sm bg-slate-50 p-3 rounded">{viewDialog.po.end_b_address}</p>
              </div>
              {viewDialog.po.sent_at && (
                <div className="border-t pt-4">
                  <p className="text-sm text-slate-500">Sent to: {viewDialog.po.sent_to}</p>
                  <p className="text-sm text-slate-500">Sent at: {new Date(viewDialog.po.sent_at).toLocaleString()}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setViewDialog({ open: false, po: null })} className="w-full sm:w-auto">Close</Button>
            <Button onClick={() => handleDownloadPDF(viewDialog.po?.po_id)} className="w-full sm:w-auto">
              <Download className="mr-2 h-4 w-4" /> Download PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Acceptance Email Dialog (with date picker) */}
      <Dialog open={acceptanceDialog.open} onOpenChange={(open) => !acceptanceDialog.submitting && setAcceptanceDialog(prev => ({ ...prev, open }))}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {acceptanceDialog.mode === 'mark_send'
                ? 'Mark Delivered & Send Acceptance'
                : (acceptanceDialog.po?.acceptance_email_status === 'sent' ? 'Resend' : 'Send') + ' Acceptance Email'}
            </DialogTitle>
            <DialogDescription>
              GTPL link acceptance for PO <span className="font-mono font-semibold">{acceptanceDialog.po?.po_number}</span>
              {acceptanceDialog.mode === 'mark_send' && ' — this will also flip the PO to Delivered.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-2">
              <Label htmlFor="acceptance-to">To <span className="text-red-500">*</span></Label>
              <Input
                id="acceptance-to"
                type="text"
                value={acceptanceDialog.to_str}
                placeholder={(defaultRecipients.to || []).join(', ') || 'recipient@example.com'}
                onChange={(e) => setAcceptanceDialog(prev => ({ ...prev, to_str: e.target.value }))}
                data-testid="acceptance-to-input"
              />
              <p className="text-xs text-slate-500">Comma-separated. Default GTPL contacts pre-filled — edit if needed.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="acceptance-cc">CC</Label>
              <Input
                id="acceptance-cc"
                type="text"
                value={acceptanceDialog.cc_str}
                placeholder={(defaultRecipients.cc || []).join(', ') || 'cc@example.com'}
                onChange={(e) => setAcceptanceDialog(prev => ({ ...prev, cc_str: e.target.value }))}
                data-testid="acceptance-cc-input"
              />
              <p className="text-xs text-slate-500">Comma-separated. Leave blank for none.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="acceptance-date">Effective Acceptance Date</Label>
              <Input
                id="acceptance-date"
                type="date"
                value={acceptanceDialog.acceptance_date}
                onChange={(e) => setAcceptanceDialog(prev => ({ ...prev, acceptance_date: e.target.value }))}
                data-testid="acceptance-date-input"
              />
              <p className="text-xs text-slate-500">
                Defaults to today ({format(new Date(), 'dd-MMM-yyyy')}). Change it to back-date or post-date the acceptance.
              </p>
            </div>
            {acceptanceDialog.po?.acceptance_email_sent_at && (
              <div className="text-xs text-slate-500 bg-slate-50 p-2 rounded border">
                Last sent: {new Date(acceptanceDialog.po.acceptance_email_sent_at).toLocaleString()}
                {acceptanceDialog.po.acceptance_date && (
                  <> · Previous effective date: <span className="font-mono">{acceptanceDialog.po.acceptance_date}</span></>
                )}
              </div>
            )}
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setAcceptanceDialog({ open: false, po: null, mode: 'send', acceptance_date: todayYMD(), to_str: '', cc_str: '', submitting: false })}
              disabled={acceptanceDialog.submitting}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              onClick={submitAcceptanceEmail}
              disabled={acceptanceDialog.submitting || !acceptanceDialog.acceptance_date}
              className="bg-indigo-600 hover:bg-indigo-700 w-full sm:w-auto"
              data-testid="confirm-acceptance-send-btn"
            >
              {acceptanceDialog.submitting ? (
                <>Sending…</>
              ) : (
                <><Send className="h-4 w-4 mr-1" />
                  {acceptanceDialog.mode === 'mark_send' ? 'Mark Delivered & Send' : 'Send Email'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Vendor Settings Dialog */}
      <Dialog open={settingsDialog} onOpenChange={setSettingsDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="vendor-settings-dialog">
          <DialogHeader>
            <DialogTitle>Vendor Settings</DialogTitle>
            <DialogDescription>Per-vendor email recipients, End A locations, and ARC price slabs.</DialogDescription>
          </DialogHeader>

          {/* GTPL Section */}
          <div className="space-y-3 border rounded-lg p-4">
            <p className="text-sm font-semibold text-slate-900">GTPL</p>
            <div className="space-y-2">
              <Label className="text-xs">GTPL Vendor Email</Label>
              <Input
                type="email"
                value={vendorSettings.vendor_emails?.GTPL || ''}
                onChange={(e) => setVendorSettings({
                  ...vendorSettings,
                  vendor_emails: { ...vendorSettings.vendor_emails, GTPL: e.target.value }
                })}
                placeholder="Enter GTPL vendor email"
                data-testid="input-gtpl-email"
              />
              <p className="text-xs text-slate-500">Approved POs will be sent to this email address.</p>
            </div>
            <div className="flex justify-end">
              <Button size="sm" onClick={handleSaveSettings} data-testid="save-vendor-settings">Save GTPL Settings</Button>
            </div>
          </div>

          {/* Anonet Section */}
          <div className="space-y-4 border-2 border-purple-200 rounded-lg p-4 bg-purple-50/30" data-testid="anonet-settings-section">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-purple-900">Anonet Communications</p>
              {anonetSettingsLoading && <span className="text-xs text-slate-500">Loading…</span>}
            </div>

            {/* Email recipients */}
            <div className="space-y-2">
              <Label className="text-xs">Default To (comma-separated)</Label>
              <Input
                value={anonetSettings.emails_to}
                onChange={(e) => setAnonetSettings(prev => ({ ...prev, emails_to: e.target.value }))}
                placeholder="ajay@anonet.in, ops@anonet.in"
                data-testid="anonet-settings-emails-to"
              />
              <Label className="text-xs mt-2">Default Cc (comma-separated)</Label>
              <Input
                value={anonetSettings.emails_cc}
                onChange={(e) => setAnonetSettings(prev => ({ ...prev, emails_cc: e.target.value }))}
                placeholder="accounts@assistivenetwork.com"
                data-testid="anonet-settings-emails-cc"
              />
              <p className="text-xs text-slate-500">Pre-fills the Send PO+SAF email dialog. Can be overridden per send.</p>
            </div>

            {/* BTS Locations */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-medium">End A — Anonet BTS Locations</Label>
                <Button size="sm" variant="outline" type="button" onClick={addAnonetBtsRow} data-testid="anonet-settings-add-bts">
                  + Add Location
                </Button>
              </div>
              <div className="space-y-2">
                {(anonetSettings.bts_locations || []).map((row) => (
                  <div key={row._id} className="grid grid-cols-12 gap-2 items-start p-2 rounded border bg-white">
                    <Input
                      className="col-span-3"
                      value={row.key}
                      onChange={(e) => updateAnonetBtsRow(row._id, { key: e.target.value })}
                      placeholder="key (eg chennai_santhome)"
                      data-testid={`anonet-bts-key-${row._id}`}
                    />
                    <Input
                      className="col-span-3"
                      value={row.code || ''}
                      onChange={(e) => updateAnonetBtsRow(row._id, { code: e.target.value })}
                      placeholder="BTS Code (eg CHN/SAN/901)"
                      data-testid={`anonet-bts-code-${row._id}`}
                    />
                    <Input
                      className="col-span-5"
                      value={row.address || ''}
                      onChange={(e) => updateAnonetBtsRow(row._id, { address: e.target.value })}
                      placeholder="Full Address"
                      data-testid={`anonet-bts-address-${row._id}`}
                    />
                    <Button
                      size="sm" variant="ghost" type="button"
                      className="col-span-1 text-red-600"
                      onClick={() => removeAnonetBtsRow(row._id)}
                      data-testid={`anonet-bts-remove-${row._id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                {(anonetSettings.bts_locations || []).length === 0 && (
                  <p className="text-xs text-slate-500">No BTS locations configured. Click "Add Location" to add one.</p>
                )}
              </div>
            </div>

            {/* Price Slabs */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-medium">ARC Price Slabs (Anonet)</Label>
                <Button size="sm" variant="outline" type="button" onClick={addAnonetSlab} data-testid="anonet-settings-add-slab">
                  + Add Slab
                </Button>
              </div>
              <div className="grid grid-cols-12 gap-2 text-xs text-slate-500 px-2">
                <div className="col-span-3">Min Bandwidth (Mbps)</div>
                <div className="col-span-3">Max Bandwidth (Mbps)</div>
                <div className="col-span-3">Rate / Mbps / Month (₹)</div>
                <div className="col-span-2">Manual ARC</div>
                <div className="col-span-1"></div>
              </div>
              <div className="space-y-2">
                {anonetSettings.price_slabs.map((slab, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center p-2 rounded border bg-white">
                    <Input
                      className="col-span-3"
                      type="number"
                      value={slab.min_bandwidth}
                      onChange={(e) => updateAnonetSlab(i, { min_bandwidth: parseFloat(e.target.value || 0) })}
                      data-testid={`anonet-slab-min-${i}`}
                    />
                    <Input
                      className="col-span-3"
                      type="number"
                      value={slab.max_bandwidth}
                      onChange={(e) => updateAnonetSlab(i, { max_bandwidth: parseFloat(e.target.value || 0) })}
                      data-testid={`anonet-slab-max-${i}`}
                    />
                    <Input
                      className="col-span-3"
                      type="number"
                      step="0.01"
                      value={slab.rate_per_mbps}
                      onChange={(e) => updateAnonetSlab(i, { rate_per_mbps: parseFloat(e.target.value || 0) })}
                      disabled={!!slab.manual_required}
                      data-testid={`anonet-slab-rate-${i}`}
                    />
                    <label className="col-span-2 flex items-center gap-2 text-xs">
                      <input
                        type="checkbox"
                        checked={!!slab.manual_required}
                        onChange={(e) => updateAnonetSlab(i, { manual_required: e.target.checked })}
                        data-testid={`anonet-slab-manual-${i}`}
                      />
                      Manual
                    </label>
                    <Button
                      size="sm" variant="ghost" type="button"
                      className="col-span-1 text-red-600"
                      onClick={() => removeAnonetSlab(i)}
                      data-testid={`anonet-slab-remove-${i}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                {anonetSettings.price_slabs.length === 0 && (
                  <p className="text-xs text-slate-500">
                    No Anonet-specific slabs. Currently using fallback (GTPL slabs or defaults). Add a slab to override.
                  </p>
                )}
              </div>
              <p className="text-xs text-slate-500">
                For bandwidths above the top max, mark a slab as <b>Manual</b> so the user is forced to enter ARC manually.
              </p>
            </div>

            <div className="flex justify-end">
              <Button
                size="sm"
                onClick={handleSaveAnonetSettings}
                className="bg-purple-700 hover:bg-purple-800"
                data-testid="save-anonet-settings"
              >
                Save Anonet Settings
              </Button>
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setSettingsDialog(false)} className="w-full sm:w-auto">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit PO Number Dialog (Admin only) */}
      <Dialog open={editPONumberDialog.open} onOpenChange={(open) => setEditPONumberDialog({ ...editPONumberDialog, open })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit PO Number</DialogTitle>
            <DialogDescription>
              Update the PO number for {editPONumberDialog.po?.customer_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Current PO Number</Label>
              <p className="font-mono text-slate-600">{editPONumberDialog.po?.po_number}</p>
            </div>
            <div className="space-y-2">
              <Label>New PO Number</Label>
              <Input
                value={editPONumberDialog.newPONumber}
                onChange={(e) => setEditPONumberDialog({ ...editPONumberDialog, newPONumber: e.target.value })}
                placeholder="Enter new PO number"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditPONumberDialog({ open: false, po: null, newPONumber: '' })}>
              Cancel
            </Button>
            <Button onClick={handleEditPONumber} className="bg-slate-900 hover:bg-slate-800">
              Update PO Number
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Update Status Dialog */}
      <Dialog open={updateStatusDialog.open} onOpenChange={(open) => setUpdateStatusDialog({ ...updateStatusDialog, open })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Update PO Status</DialogTitle>
            <DialogDescription>
              Update delivery workflow status for {updateStatusDialog.po?.po_number}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Current Status</Label>
              <Badge className={STATUS_COLORS[updateStatusDialog.po?.status]}>
                {STATUS_LABELS[updateStatusDialog.po?.status]}
              </Badge>
            </div>
            <div className="space-y-2">
              <Label>New Status</Label>
              <Select 
                value={updateStatusDialog.newStatus} 
                onValueChange={(v) => setUpdateStatusDialog({ ...updateStatusDialog, newStatus: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select new status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="provider_processed">Provider Processed</SelectItem>
                  <SelectItem value="ra_generated">RA Generated</SelectItem>
                  <SelectItem value="constraint">Constraint</SelectItem>
                  <SelectItem value="ready_for_delivery">Ready for Delivery</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {updateStatusDialog.newStatus === 'ra_generated' && (
              <div className="space-y-2">
                <Label>RA Number</Label>
                <Input
                  value={updateStatusDialog.ra_number}
                  onChange={(e) => setUpdateStatusDialog({ ...updateStatusDialog, ra_number: e.target.value })}
                  placeholder="Enter RA number"
                  data-testid="status-ra-number-input"
                />
              </div>
            )}
            {updateStatusDialog.newStatus === 'constraint' && (
              <div className="space-y-2">
                <Label>Reason for Constraint *</Label>
                <Textarea
                  value={updateStatusDialog.reason}
                  onChange={(e) => setUpdateStatusDialog({ ...updateStatusDialog, reason: e.target.value })}
                  placeholder="Describe the constraint..."
                  rows={3}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUpdateStatusDialog({ open: false, po: null, newStatus: '', reason: '', ra_number: '' })}>
              Cancel
            </Button>
            <Button onClick={handleUpdateStatus} className="bg-slate-900 hover:bg-slate-800">
              Update Status
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remap PO Dialog (Admin only) */}
      <Dialog
        open={remapDialog.open}
        onOpenChange={(open) => !remapDialog.submitting && setRemapDialog(prev => ({ ...prev, open }))}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Remap Purchase Order</DialogTitle>
            <DialogDescription>
              Re-link <span className="font-mono font-semibold">{remapDialog.po?.po_number}</span> to a different customer / circuit.
              Useful when the original circuit was deleted (e.g. duplicate cleanup).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="bg-slate-50 border rounded p-3 text-xs space-y-1">
              <p><span className="text-slate-500">Current customer:</span> <span className="font-medium">{remapDialog.po?.customer_name || '—'}</span></p>
              <p><span className="text-slate-500">Current circuit ID:</span> <span className="font-mono">{remapDialog.po?.circuit_id || '—'}</span></p>
            </div>
            <div className="space-y-2">
              <Label>New Customer *</Label>
              <Select
                value={remapDialog.customer_id}
                onValueChange={handleRemapCustomerChange}
              >
                <SelectTrigger data-testid="remap-customer-select">
                  <SelectValue placeholder="Select customer" />
                </SelectTrigger>
                <SelectContent>
                  {customers.map((c) => (
                    <SelectItem key={c.customer_id} value={c.customer_id}>{c.company_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>New Circuit *</Label>
              <Select
                value={remapDialog.circuit_id}
                onValueChange={(v) => setRemapDialog(prev => ({ ...prev, circuit_id: v }))}
                disabled={!remapDialog.customer_id || remapCircuitsLoading}
              >
                <SelectTrigger data-testid="remap-circuit-select">
                  <SelectValue placeholder={
                    !remapDialog.customer_id ? 'Select customer first' :
                    remapCircuitsLoading ? 'Loading…' :
                    remapCircuits.length === 0 ? 'No circuits for this customer' :
                    'Select circuit'
                  } />
                </SelectTrigger>
                <SelectContent>
                  {remapCircuits.map((c) => (
                    <SelectItem key={c.circuit_id} value={c.circuit_id}>
                      {c.ant_circuit_id || c.circuit_id} {c.location_name ? `- ${c.location_name}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">PO number, ARC, RA number and history are preserved. End B address is rebuilt from the new circuit.</p>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setRemapDialog({ open: false, po: null, customer_id: '', circuit_id: '', submitting: false })}
              disabled={remapDialog.submitting}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              onClick={submitRemap}
              disabled={remapDialog.submitting || !remapDialog.customer_id || !remapDialog.circuit_id}
              className="bg-amber-600 hover:bg-amber-700 w-full sm:w-auto"
              data-testid="confirm-remap-btn"
            >
              {remapDialog.submitting ? 'Remapping…' : (<><Link2 className="h-4 w-4 mr-1" /> Remap PO</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Anonet PO + SAF Send Email Dialog */}
      <Dialog open={anonetSendDialog.open} onOpenChange={(open) => setAnonetSendDialog(prev => ({ ...prev, open }))}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="anonet-send-dialog">
          <DialogHeader>
            <DialogTitle>Send Anonet PO + SAF</DialogTitle>
            <DialogDescription>
              PO: <span className="font-mono">{anonetSendDialog.po?.po_number}</span> · Customer:{' '}
              {anonetSendDialog.po?.customer_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">To (comma-separated)</Label>
                <Input
                  value={anonetSendDialog.to_str}
                  onChange={(e) => setAnonetSendDialog(prev => ({ ...prev, to_str: e.target.value }))}
                  placeholder={(anonetDefaultRecipients.to || []).join(', ')}
                  data-testid="anonet-to"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Cc (comma-separated)</Label>
                <Input
                  value={anonetSendDialog.cc_str}
                  onChange={(e) => setAnonetSendDialog(prev => ({ ...prev, cc_str: e.target.value }))}
                  placeholder={(anonetDefaultRecipients.cc || []).join(', ')}
                  data-testid="anonet-cc"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Latitude</Label>
                <Input
                  value={anonetSendDialog.latitude}
                  onChange={(e) => setAnonetSendDialog(prev => ({ ...prev, latitude: e.target.value }))}
                  placeholder="e.g. 12.9716 (auto-filled from circuit)"
                  data-testid="anonet-latitude"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Longitude</Label>
                <Input
                  value={anonetSendDialog.longitude}
                  onChange={(e) => setAnonetSendDialog(prev => ({ ...prev, longitude: e.target.value }))}
                  placeholder="e.g. 77.5946 (auto-filled from circuit)"
                  data-testid="anonet-longitude"
                />
              </div>
            </div>
            <p className="text-xs text-slate-500">
              If both lat/long are present they are added to the email body with a Google Maps link.
            </p>

            <div className="space-y-2 p-3 border rounded-lg bg-purple-50/40 border-purple-200">
              <p className="text-sm font-medium text-purple-900">Address Proof Attachment</p>
              {anonetCustomerFiles.length > 0 && (
                <div className="space-y-1">
                  <Label className="text-xs">Pick from customer KYC documents</Label>
                  <Select
                    value={anonetSendDialog.address_proof_file_id}
                    onValueChange={(v) => setAnonetSendDialog(prev => ({
                      ...prev, address_proof_file_id: v, address_proof_b64: '', address_proof_filename: '',
                    }))}
                  >
                    <SelectTrigger data-testid="anonet-kyc-select">
                      <SelectValue placeholder="Choose a KYC document (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {anonetCustomerFiles.map(f => (
                        <SelectItem key={f.id} value={f.id}>
                          {f.filename} {f.category ? `· ${f.category}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-1">
                <Label className="text-xs">…or upload a new file (overrides KYC pick)</Label>
                <Input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.webp"
                  onChange={(e) => handleAnonetAddressProofUpload(e.target.files?.[0])}
                  data-testid="anonet-address-proof-upload"
                />
                {anonetSendDialog.address_proof_filename && !anonetSendDialog.address_proof_file_id && (
                  <p className="text-xs text-emerald-700 mt-1">
                    Will attach: <b>{anonetSendDialog.address_proof_filename}</b>
                  </p>
                )}
              </div>
            </div>

            <details className="space-y-2 p-3 border rounded-lg">
              <summary className="cursor-pointer text-sm font-medium text-slate-900">Edit SAF B-End details (optional)</summary>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3">
                <div className="space-y-1">
                  <Label className="text-xs">B-End Contact Name</Label>
                  <Input value={anonetSendDialog.saf_b_contact_name}
                    onChange={(e) => setAnonetSendDialog(prev => ({ ...prev, saf_b_contact_name: e.target.value }))}
                    data-testid="anonet-saf-b-contact" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">B-End Company / Dept</Label>
                  <Input value={anonetSendDialog.saf_b_dept_name}
                    onChange={(e) => setAnonetSendDialog(prev => ({ ...prev, saf_b_dept_name: e.target.value }))}
                    data-testid="anonet-saf-b-dept" />
                </div>
                <div className="space-y-1 sm:col-span-2">
                  <Label className="text-xs">B-End Address</Label>
                  <Textarea rows={2} value={anonetSendDialog.saf_b_address}
                    onChange={(e) => setAnonetSendDialog(prev => ({ ...prev, saf_b_address: e.target.value }))}
                    data-testid="anonet-saf-b-address" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">B-End City</Label>
                  <Input value={anonetSendDialog.saf_b_city}
                    onChange={(e) => setAnonetSendDialog(prev => ({ ...prev, saf_b_city: e.target.value }))}
                    data-testid="anonet-saf-b-city" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">B-End State</Label>
                  <Input value={anonetSendDialog.saf_b_state}
                    onChange={(e) => setAnonetSendDialog(prev => ({ ...prev, saf_b_state: e.target.value }))}
                    data-testid="anonet-saf-b-state" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">B-End Landmark</Label>
                  <Input value={anonetSendDialog.saf_b_landmark}
                    onChange={(e) => setAnonetSendDialog(prev => ({ ...prev, saf_b_landmark: e.target.value }))}
                    data-testid="anonet-saf-b-landmark" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">B-End Email</Label>
                  <Input value={anonetSendDialog.saf_b_email}
                    onChange={(e) => setAnonetSendDialog(prev => ({ ...prev, saf_b_email: e.target.value }))}
                    data-testid="anonet-saf-b-email" />
                </div>
                <div className="space-y-1 sm:col-span-2">
                  <Label className="text-xs">Purpose</Label>
                  <Input value={anonetSendDialog.saf_purpose}
                    onChange={(e) => setAnonetSendDialog(prev => ({ ...prev, saf_purpose: e.target.value }))}
                    data-testid="anonet-saf-purpose" />
                </div>
              </div>
            </details>

            {anonetSendDialog.po?.saf_email_sent_at && (
              <p className="text-xs text-slate-500">
                Last sent: {new Date(anonetSendDialog.po.saf_email_sent_at).toLocaleString()}
                {anonetSendDialog.po?.saf_email_sent_to ? ` → ${anonetSendDialog.po.saf_email_sent_to}` : ''}
              </p>
            )}
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setAnonetSendDialog(prev => ({ ...prev, open: false }))}
              disabled={anonetSendDialog.submitting}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={() => anonetSendDialog.po && handleDownloadSaf(anonetSendDialog.po.po_id)}
              className="w-full sm:w-auto"
              data-testid="anonet-preview-saf-btn"
            >
              <FileText className="h-4 w-4 mr-1" /> Preview SAF
            </Button>
            <Button
              onClick={submitAnonetSend}
              disabled={anonetSendDialog.submitting}
              className="bg-purple-700 hover:bg-purple-800 w-full sm:w-auto"
              data-testid="anonet-send-submit"
            >
              {anonetSendDialog.submitting ? 'Sending…' : (<><Send className="h-4 w-4 mr-1" /> Send PO + SAF</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
